import React, { ReactNode, lazy } from 'react'
import {
  DotsLoader,
  BrandingFooter,
  WarningPage,
  ScreenReaderOnly,
} from '@chilipiper/design-system'
import { usePageTitle } from '@chilipiper/booking-flow-legacy'
import { getSessionId } from '@chilipiper/service/src/session'
import { CloseButton } from './CloseButton'
import { useGuestContext } from './guestContext'

const BookedFooter = lazy(() => import('../footer/BookedFooter'))
const CriticalError = lazy(() => import('../../availability/CriticalError'))
const AlreadyCanceled = lazy(() => import('../../cancel-meeting/AlreadyCanceled'))
const RenderError = lazy(() => import('../../availability/OnlineErrors'))

interface Props {
  children: ReactNode
}

export const GuestWrapper = ({ children }: Props) => {
  const {
    loadingError,
    loading,
    cancelled,
    navigatedError,
    marketing,
    footer,
    component,
    bookedOrRequested,
    isInIframe,
    isIntermediate,
    hideCloseButton,
    processRedirect,
    closeRedirect,
    singleUseLinkData,
    isSingleUseLinkExpired,
    isSingleUseLinkBooked,
    closeButtonVisible,
  } = useGuestContext()
  usePageTitle()

  const sessionId = getSessionId()

  if (loading || (!isIntermediate && !sessionId)) {
    return (
      <>
        <ScreenReaderOnly role='status'>Loading Page</ScreenReaderOnly>
        <DotsLoader color='neutral-dark-32' height='100%' />
      </>
    )
  }

  if (loadingError) {
    return <CriticalError />
  }

  if (!singleUseLinkData || isSingleUseLinkExpired) {
    return <WarningPage showFooter={true} message='This page is not available' />
  }

  if (isSingleUseLinkBooked) {
    return (
      <WarningPage
        showFooter={true}
        message='This meeting is now in the past and you can no longer reschedule. Contact the meeting owner if you wish to book another meeting.'
      />
    )
  }

  if (cancelled) {
    return <AlreadyCanceled />
  }

  if (navigatedError) {
    return <RenderError code={navigatedError.errorCode} message={navigatedError.errorMessage} />
  }

  const layoutGuestClassName = 'layout-guest'
  const middleClassName = 'middle'
  const shouldShowCloseButton =
    isInIframe && !hideCloseButton && (component !== 'booked' || closeButtonVisible)

  return (
    <div className={layoutGuestClassName} data-test-id='container'>
      <div className={`${middleClassName} middle-guest`}>
        <main className='content'>{children}</main>
      </div>
      {shouldShowPoweredByChili({ isInIframe, marketing, footer, bookedOrRequested }) && (
        <BrandingFooter anchorLink={getPoweredByChiliLink({ isInIframe, component })} />
      )}
      {bookedOrRequested && !isInIframe && <BookedFooter />}
      {shouldShowCloseButton ? (
        <CloseButton
          inIframe={isInIframe}
          processRedirect={processRedirect}
          closeRedirect={closeRedirect}
          component={component}
        />
      ) : null}
    </div>
  )
}

const shouldShowPoweredByChili = ({
  isInIframe,
  marketing,
  bookedOrRequested,
  footer,
}: {
  bookedOrRequested: boolean
  footer: boolean
  isInIframe: boolean
  marketing: boolean
}) => !!(isInIframe || (footer && !marketing && !bookedOrRequested))

const getPoweredByChiliLink = ({
  isInIframe,
  component,
}: {
  component: string
  isInIframe: boolean
}) => {
  if (isInIframe) {
    return (
      {
        calendar: createCampaignLink('conciergescreen-calendar'),
        booked: createCampaignLink('conciergescreen-confirmation'),
      }[component] ?? createCampaignLink('conciergescreen')
    )
  }
  return createCampaignLink('bookinglink')
}

const POWERED_BY_CHILI_LINK =
  'https://www.chilipiper.com/page/powered-by-chili-piper?utm_medium=product&utm_source=vir'
const createCampaignLink = (campaign: string) => `${POWERED_BY_CHILI_LINK}&utm_campaign=${campaign}`
